import clsx from 'clsx';
import React, { memo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton } from '@components/Button/variants';
import useCurrentNewsletterSubscription from '@hooks/useCurrentNewsletterSubscription';
import useEventTracking from '@hooks/useEventTracking';
import useNewsletter from '@hooks/useNewsletter';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import { newsletter_list_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

const BUTTON_TEXT = {
  SUBSCRIBE_NOW: 'Subscribe now',
  SUBSCRIBED: 'Subscribed',
  VIEW_LIST: 'View list',
};
const buttonClassName = 'max-sm:!w-fit mt-8';

interface SubscribeToNewsletterProps {
  id: string | number;
}

const SubscribeToNewsletter = ({ id: newsletterId }: SubscribeToNewsletterProps): JSX.Element => {
  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { isSubscribed: isSubscribedToNewsletter, isLoadingSubscribed } =
    useCurrentNewsletterSubscription(newsletterId, !successfullySubscribed);
  const { newsletter, isLoadingNewsletter } = useNewsletter(newsletterId);
  const asideContent = isSubscribedToNewsletter
    ? newsletter?.contentForNewsletterAsideSubscribed
    : newsletter?.contentForNewsletterAsideNotSubscribed;
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SubscribeToNewsletter });
  const { mutate: subscribeToNewsletter, isLoading: isSubscribeLoading } =
    useSubscribeToNewsletter();
  const subscribeButtonText = successfullySubscribed
    ? BUTTON_TEXT.SUBSCRIBED
    : BUTTON_TEXT.SUBSCRIBE_NOW;

  const handleSubscribe = () => {
    try {
      subscribeToNewsletter({ newsletter_id: newsletterId });
      setSuccessfullySubscribed(true);
      trackEvent(click({ clickTarget: ClickTarget.Subscribe, clickText: subscribeButtonText }));
    } catch (err) {
      setSuccessfullySubscribed(false);
    }
  };

  return (
    <aside
      className={`${
        isLoadingSubscribed || isLoadingNewsletter ? 'opacity-0' : 'opacity-100'
      } relative mt-80 transition-opacity print:hidden md:-mt-[168px]`}
      data-testid="newsletter-article"
    >
      {!isLoadingSubscribed && !isLoadingNewsletter && (
        <>
          {!isSubscribedToNewsletter && (
            <span className="block font-suisse-intl-mono text-overline-sm text-grayscale-gray">
              {asideContent?.frequency}
            </span>
          )}
          <h3 className="mb-0 mt-8 font-suisse-works text-headline-xxs text-black">
            {asideContent?.name}
          </h3>
          <p className="mb-0 mt-4 font-suisse-intl text-button-sm font-normal text-grayscale-gray">
            {ReactHtmlParser(asideContent?.description)}
          </p>
          {isSubscribedToNewsletter ? (
            <PrimaryButton
              as="link"
              size="small"
              className={buttonClassName}
              href={newsletter_list_path()}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ViewList }))}
            >
              {BUTTON_TEXT.VIEW_LIST}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              as="button"
              size="small"
              className={clsx(buttonClassName, {
                'disabled:bg-white disabled:text-grayscale-gray': successfullySubscribed,
              })}
              onClick={handleSubscribe}
              disabled={isSubscribeLoading || successfullySubscribed}
            >
              {subscribeButtonText}
            </PrimaryButton>
          )}
        </>
      )}
    </aside>
  );
};

export default memo(SubscribeToNewsletter);
